import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import { supabase } from '../../../api/supabase';
import { 
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    CalendarOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { 
    Table,
    Button,
    Card,
    Typography,
    Space,
    Badge,
    Tag,
    Input,
    DatePicker,
    Select,
    Form,
    Modal,
    Drawer,
    Spin,
    Alert,
    InputNumber,
    Empty
} from 'antd';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/fr';


const { Title, Text } = Typography;
const { TextArea } = Input;

const ORDER_STATUS = {
    IN_PROGRESS: 'En cours',
    SUBMITTED: 'Soumis',
    DELIVERED: 'Délivré'
};

const ITEMS_PER_PAGE = 10;


// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Set the default timezone
dayjs.tz.setDefault("America/St_Barthelemy");

// Update your date utility functions
// Modify these utility functions
const getTodayDate = () => {
    // This ensures we get the date in St Barth timezone
    return dayjs().tz("America/St_Barthelemy").format('YYYY-MM-DD');
};

const formatDateForDB = (date) => {
    // Convert the selected date to St Barth timezone and format for DB
    if (!date) return null;
    return dayjs(date).tz("America/St_Barthelemy").format('YYYY-MM-DD');
};

const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    return dayjs(dateStr).tz("America/St_Barthelemy").format('DD/MM/YYYY');
};

const OrderManagementPage = () => {
    // Keep your existing state declarations
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [newOrder, setNewOrder] = useState({
        title: '',
        order_date: getTodayDate(),
        status: ORDER_STATUS.IN_PROGRESS,
        notes: '',
        products: []
    });
    const [showModal, setShowModal] = useState(false);
    const [showDrawer, setShowDrawer] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editingOrder, setEditingOrder] = useState(null);
    const [actionType, setActionType] = useState('');
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        fetchOrders();
        fetchProducts();
    }, []);

    useEffect(() => {
        if (searchTerm) {
            const filtered = products.filter(product => 
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.provider_name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredProducts(filtered);
        } else {
            setFilteredProducts([]);
        }
    }, [searchTerm, products]);

    useEffect(() => {
        fetchOrders();
    }, [currentPage]);

    const fetchProducts = async () => {
        const { data, error } = await supabase
            .from('products')
            .select('*')
            .order('name');
            
        if (error) {
            console.error("Error fetching products:", error);
        } else {
            setProducts(data);
        }
    };const fetchOrders = async () => {
        setLoading(true);
        setIsError(false);
        setErrorMessage('');
    
        try {
            // First, get total count for pagination
            const { count, error: countError } = await supabase
                .from('orders')
                .select('*', { count: 'exact', head: true });
    
            if (countError) throw countError;
            setTotalCount(count || 0);
    
            // Then fetch the actual data
            const { data, error } = await supabase
                .from('orders')
                .select(`
                    *,
                    items:order_items (
                        id,
                        quantity,
                        product:products (
                            id,
                            name,
                            provider_name,
                            price
                        )
                    )
                `)
                .order('created_at', { ascending: false })
                .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);
    
            if (error) throw error;
    
            if (!data || data.length === 0) {
                console.log('No orders found');
                setOrders([]);
            } else {
                const processedOrders = data.map(order => {
                    const totalAmount = order.items.reduce((sum, item) => {
                        return sum + (item.quantity * item.product.price);
                    }, 0);
                
                    return {
                        ...order,
                        order_items: order.items,
                        calculated_total: totalAmount,
                        formatted_date: formatDateForDisplay(order.order_date), // Use the new display formatter
                    };
                });
    
                setOrders(processedOrders);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            setIsError(true);
            setErrorMessage(error.message || 'Failed to fetch orders');
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSubmit = async () => {
        await handleAddOrder();
    };

    const handleAddOrder = async () => {
        setLoading(true);
    
        try {
            const calculatedTotal = selectedProducts.reduce((total, item) => {
                return total + (item.product.price * item.quantity);
            }, 0);
    
            // Use formatDateForDB when saving to database
            const { data: orderData, error: orderError } = await supabase
                .from('orders')
                .insert([{
                    title: newOrder.title,
                    order_date: formatDateForDB(newOrder.order_date),  // Use the new function
                    status: newOrder.status,
                    total_amount: calculatedTotal,
                    notes: newOrder.notes
                }])
                .select()
                .single();

            if (orderError) throw orderError;

            // Then, insert order items
            if (selectedProducts.length > 0) {
                const orderItems = selectedProducts.map(item => ({
                    order_id: orderData.id,
                    product_id: item.product.id,
                    quantity: item.quantity
                }));

                const { error: itemsError } = await supabase
                    .from('order_items')
                    .insert(orderItems);

                if (itemsError) throw itemsError;
            }

            setNewOrder({ 
                title: '', 
                order_date: getTodayDate(), 
                status: ORDER_STATUS.IN_PROGRESS, 
                notes: '' 
            });
            setSelectedProducts([]);
            await fetchOrders();
            setShowDrawer(false);
        } catch (error) {
            console.error("Error adding order:", error);
            setIsError(true);
            setErrorMessage(error.message || 'Failed to add order');
        } finally {
            setLoading(false);
        }
    };

    const addProductToOrder = (product) => {
        const existingProduct = selectedProducts.find(p => p.product.id === product.id);
        
        if (existingProduct) {
            setSelectedProducts(selectedProducts.map(p => 
                p.product.id === product.id 
                    ? { ...p, quantity: p.quantity + 1 }
                    : p
            ));
        } else {
            setSelectedProducts([...selectedProducts, { product, quantity: 1 }]);
        }
        setSearchTerm('');
        setFilteredProducts([]);
    };

    const updateProductQuantity = (productId, quantity) => {
        if (quantity <= 0) {
            setSelectedProducts(selectedProducts.filter(p => p.product.id !== productId));
        } else {
            setSelectedProducts(selectedProducts.map(p => 
                p.product.id === productId 
                    ? { ...p, quantity }
                    : p
            ));
        }
    };

    const removeProduct = (productId) => {
        setSelectedProducts(selectedProducts.filter(p => p.product.id !== productId));
    };

    const confirmDeleteOrder = async () => {
        try {
            const { error } = await supabase
                .from('orders')
                .delete()
                .eq('id', selectedOrder.id);
            
            if (error) throw error;
            
            await fetchOrders();
            closeModal();
        } catch (error) {
            console.error("Error deleting order:", error);
            setIsError(true);
            setErrorMessage(error.message || 'Failed to delete order');
        }
    };

    const confirmEditOrder = async () => {
        if (!editingOrder) return;
        
        try {
            const { error } = await supabase
                .from('orders')
                .update({
                    title: editingOrder.title,
                    order_date: formatDateForDB(editingOrder.order_date),  // Use the new function
                    status: editingOrder.status,
                    notes: editingOrder.notes
                })
                .eq('id', editingOrder.id);
            
            if (error) throw error;
            
            await fetchOrders();
            closeModal();
        } catch (error) {
            console.error("Error updating order:", error);
            setIsError(true);
            setErrorMessage(error.message || 'Failed to update order');
        }
    };

    const openModal = (order, type) => {
        setSelectedOrder(order);
        if (type === 'edit') {
            setEditingOrder({ ...order });
        }
        setActionType(type);
        setShowModal(true);
    };

    const closeModal = () => {
        setSelectedOrder(null);
        setEditingOrder(null);
        setActionType('');
        setShowModal(false);
    };

    const columns = [
        {
            title: 'Commande',
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => (
                <div>
                    <Text strong>{text}</Text>
                    {record.notes && (
                        <div>
                            <Text type="secondary" className="text-sm">{record.notes}</Text>
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'formatted_date',
            key: 'formatted_date',
            render: (text) => (
                <Space>
                    <CalendarOutlined />
                    <Text>{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'default';
                if (status === ORDER_STATUS.IN_PROGRESS) color = 'processing';
                else if (status === ORDER_STATUS.SUBMITTED) color = 'warning';
                else if (status === ORDER_STATUS.DELIVERED) color = 'success';
                return <Badge status={color} text={status} />;
            },
        },
        {
            title: 'Produits',
            dataIndex: 'order_items',
            key: 'order_items',
            render: (items) => (
                <Space direction="vertical" size="small">
                    {items?.map((item) => (
                        <div key={`${item.product.id}`}>
                            <Tag color="blue">×{item.quantity}</Tag>
                            <Text>{item.product.name}</Text>
                        </div>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Total',
            dataIndex: 'calculated_total',
            key: 'calculated_total',
            render: (value) => <Text strong>{value?.toFixed(2)}€</Text>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openModal(record, 'edit')}
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => openModal(record, 'delete')}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div style={{ background: '#f0f2f5', minHeight: '100vh', padding: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Header */}
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <Title level={4} style={{ marginBottom: 4 }}>Gestion des commandes</Title>
                            <Text type="secondary">{totalCount} commande{totalCount !== 1 ? 's' : ''} au total</Text>
                        </div>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={() => setShowDrawer(true)}
                        >
                            Nouvelle commande
                        </Button>
                    </div>
                </Card>

                {/* Error Alert */}
                {isError && (
                    <Alert
                        message={errorMessage}
                        type="error"
                        showIcon
                        closable
                    />
                )}

                {/* Orders Table */}
                <Card>
                    <Table
                        columns={columns}
                        dataSource={orders}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            current: currentPage,
                            pageSize: ITEMS_PER_PAGE,
                            total: totalCount,
                            onChange: handlePageChange,
                        }}
                        locale={{
                            emptyText: (
                                <Empty
                                    description="Aucune commande"
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                >
                                    <Button 
                                        type="primary" 
                                        onClick={() => setShowDrawer(true)}
                                        icon={<PlusOutlined />}
                                    >
                                        Nouvelle commande
                                    </Button>
                                </Empty>
                            )
                        }}
                    />
                </Card>
            </Space>

            {/* Add Order Drawer */}
            <Drawer
                title="Nouvelle commande"
                placement="right"
                width={520}
                open={showDrawer}
                onClose={() => setShowDrawer(false)}
                extra={
                    <Space>
                        <Button onClick={() => setShowDrawer(false)}>Annuler</Button>
                        <Button 
                            type="primary" 
                            onClick={handleSubmit}
                            loading={loading}
                        >
                            Ajouter
                        </Button>
                    </Space>
                }
            >
                <Form layout="vertical">
                    <Form.Item label="Titre" required>
                        <Input
                            value={newOrder.title}
                            onChange={(e) => setNewOrder({ ...newOrder, title: e.target.value })}
                            placeholder="Titre de la commande"
                        />
                    </Form.Item>

                    <Form.Item label="Rechercher un produit">
                        <Input
                            prefix={<SearchOutlined />}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Nom du produit..."
                        />
                        {filteredProducts.length > 0 && (
                            <Card style={{ marginTop: 8 }}>
                                {filteredProducts.map(product => (
                                    <div
                                        key={product.id}
                                        onClick={() => addProductToOrder(product)}
                                        style={{ padding: '8px', cursor: 'pointer' }}
                                        className="hover:bg-gray-50"
                                    >
                                        <Text strong>{product.name}</Text>
                                        <br />
                                        <Text type="secondary">{product.provider_name}</Text>
                                    </div>
                                ))}
                            </Card>
                        )}
                    </Form.Item>

                    {selectedProducts.length > 0 && (
                        <Form.Item label="Produits sélectionnés">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {selectedProducts.map(item => (
                                    <Card key={item.product.id} size="small">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text>{item.product.name}</Text>
                                            <Space>
                                                <InputNumber
                                                    min={1}
                                                    value={item.quantity}
                                                    onChange={(value) => updateProductQuantity(item.product.id, value)}
                                                />
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => removeProduct(item.product.id)}
                                                />
                                            </Space>
                                        </div>
                                    </Card>
                                ))}
                            </Space>
                        </Form.Item>
                    )}

<Form.Item label="Date de commande" required>
    <DatePicker
        style={{ width: '100%' }}
        value={newOrder.order_date ? dayjs(newOrder.order_date) : dayjs()}
        onChange={(date) => setNewOrder({
            ...newOrder,
            order_date: date ? date.toISOString() : getTodayDate()
        })}
        format="DD/MM/YYYY"
    />
</Form.Item>


                    <Form.Item label="Statut" required>
                        <Select
                            value={newOrder.status}
                            onChange={(value) => setNewOrder({ ...newOrder, status: value })}
                            style={{ width: '100%' }}
                        >
                            {Object.values(ORDER_STATUS).map(status => (
                                <Select.Option key={status} value={status}>
                                    {status}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Notes">
                        <TextArea
                            value={newOrder.notes}
                            onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })}
                            rows={4}
                            placeholder="Notes additionnelles..."
                        />
                    </Form.Item>
                </Form>
            </Drawer>

            {/* Delete Modal */}
            <Modal
                title="Confirmer la suppression"
                open={showModal && actionType === 'delete'}
                onOk={confirmDeleteOrder}
                onCancel={closeModal}
                okText="Supprimer"
                cancelText="Annuler"
                okButtonProps={{ danger: true }}
            >
                <p>Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible.</p>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Modifier la commande"
                open={showModal && actionType === 'edit'}
                onOk={confirmEditOrder}
                onCancel={closeModal}
                okText="Sauvegarder"
                cancelText="Annuler"
            >
                <Form layout="vertical">
                    <Form.Item label="Titre">
                        <Input
                            value={editingOrder?.title}
                            onChange={(e) => setEditingOrder({
                                ...editingOrder,
                                title: e.target.value
                            })}
                        />
                    </Form.Item>

                    <Form.Item label="Date">
    <DatePicker
        style={{ width: '100%' }}
        value={editingOrder?.order_date ? dayjs(editingOrder.order_date) : null}
        onChange={(date) => setEditingOrder({
            ...editingOrder,
            order_date: date ? date.toISOString() : null
        })}
        format="DD/MM/YYYY"
    />
</Form.Item>

                    <Form.Item label="Statut">
                        <Select
                            value={editingOrder?.status}
                            onChange={(value) => setEditingOrder({
                                ...editingOrder,
                                status: value
                            })}
                            style={{ width: '100%' }}
                        >
                            {Object.values(ORDER_STATUS).map(status => (
                                <Select.Option key={status} value={status}>
                                    {status}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Notes">
                        <TextArea
                            value={editingOrder?.notes}
                            onChange={(e) => setEditingOrder({
                                ...editingOrder,
                                notes: e.target.value
                            })}
                            rows={3}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default OrderManagementPage;